/*
 * Petropy: product hero gallery controls.
 *  - swipe left/right on the main image to move to the next/previous picture
 *  - left/right arrows on the main image
 *  - left/right arrows on the thumbnail strip when it scrolls sideways (mobile)
 *
 * Kept separate from the Dropmagic files (dm-*.js, dm-product-hero-2.liquid) because those are
 * generated and can be overwritten. This only reuses the hero's own handleMediaClick(), the same
 * function that runs when a thumbnail is tapped, so images and videos swap exactly as they do today.
 */
(() => {
  const SWIPE_MIN_PX = 40;

  const main = document.getElementById('product-hero-main-image');
  const hero = document.querySelector('dm-product-hero-section');
  if (!main || !hero) return;

  const pathOf = (url) => {
    try {
      return new URL(url, location.href).pathname;
    } catch {
      return url;
    }
  };

  const srcOf = (el) => {
    const media = el.querySelector('img, video source');
    return media ? pathOf(media.getAttribute('src') || '') : '';
  };

  const getGalleries = () => [...document.querySelectorAll('[data-dm-element="product-images-gallery"]')];

  // Every gallery lists the product media; use the fullest one as the ordered list of pictures.
  const getItems = () => {
    const lists = getGalleries().map((gallery) => [...gallery.querySelectorAll(':scope > [data-media-id]')]);
    return lists.sort((a, b) => b.length - a.length)[0] || [];
  };

  // The main image can also be changed by a thumbnail tap or the variant picker, so read what is showing.
  const currentIndex = (items) => {
    const shown = srcOf(main);
    const index = items.findIndex((item) => srcOf(item) === shown);
    return index === -1 ? 0 : index;
  };

  const revealThumb = (thumb) => {
    const strip = thumb.parentElement;
    if (!strip || strip.scrollWidth <= strip.clientWidth) return;
    const stripRect = strip.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    strip.scrollBy({
      left: thumbRect.left - stripRect.left - (stripRect.width - thumbRect.width) / 2,
      behavior: 'smooth'
    });
  };

  const step = (direction) => {
    const items = getItems();
    if (items.length < 2) return;
    const next = items[(currentIndex(items) + direction + items.length) % items.length];
    hero.handleMediaClick(next);
    revealThumb(next);
  };

  /* ---------- Arrows ---------- */

  const style = document.createElement('style');
  style.textContent = `
    .petropy-arrow {
      appearance: none;
      position: absolute;
      top: 50%;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      min-height: 0;
      margin: 0;
      padding: 0;
      border: 0;
      border-radius: 50%;
      background: none;
      cursor: pointer;
      transform: translateY(-50%);
      -webkit-tap-highlight-color: transparent;
    }
    .petropy-arrow svg { width: 28px; height: 28px; }
    .petropy-arrow--prev { left: 6px; }
    .petropy-arrow--next { right: 6px; }
    .petropy-arrow--main { color: #fff; }
    .petropy-arrow--main svg { filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.5)); }
    .petropy-arrow--strip { color: #111; background: rgba(255, 255, 255, 0.9); width: 36px; height: 36px; }
    .petropy-arrow--strip svg { width: 22px; height: 22px; }
    .petropy-arrow[hidden] { display: none; }
    .petropy-strip { position: relative; }
  `;
  document.head.appendChild(style);

  const chevron = { prev: 'M15 4 7 12l8 8', next: 'M9 4l8 8-8 8' };

  const makeArrow = (direction, variant, label) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `petropy-arrow petropy-arrow--${direction} petropy-arrow--${variant}`;
    button.setAttribute('aria-label', label);
    button.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" ' +
      `stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${chevron[direction]}"/></svg>`;
    return button;
  };

  // Main image arrows
  if (getItems().length > 1) {
    const prev = makeArrow('prev', 'main', 'Previous image');
    const next = makeArrow('next', 'main', 'Next image');
    prev.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));
    main.append(prev, next);
  }

  // Thumbnail strip arrows: only shown while the strip actually scrolls sideways.
  const strips = getGalleries().map((strip) => {
    const wrap = document.createElement('div');
    wrap.className = 'petropy-strip';
    strip.parentNode.insertBefore(wrap, strip);
    wrap.appendChild(strip);

    const prev = makeArrow('prev', 'strip', 'Scroll thumbnails left');
    const next = makeArrow('next', 'strip', 'Scroll thumbnails right');
    prev.addEventListener('click', () => strip.scrollBy({ left: -strip.clientWidth * 0.8, behavior: 'smooth' }));
    next.addEventListener('click', () => strip.scrollBy({ left: strip.clientWidth * 0.8, behavior: 'smooth' }));
    wrap.append(prev, next);

    const update = () => {
      // The wrapper stays in the layout even when Dropmagic hides the strip for this breakpoint,
      // so mirror the strip's own display to avoid an empty gap.
      wrap.style.display = getComputedStyle(strip).display === 'none' ? 'none' : '';
      const scrolls = strip.scrollWidth > strip.clientWidth + 1;
      prev.hidden = !scrolls || strip.scrollLeft <= 1;
      next.hidden = !scrolls || strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 1;
    };

    strip.addEventListener('scroll', update, { passive: true });
    update();
    return update;
  });

  window.addEventListener('resize', () => strips.forEach((update) => update()));
  window.addEventListener('load', () => strips.forEach((update) => update()));

  /* ---------- Swipe ---------- */

  // Let the browser keep handling vertical scrolling; we only react to horizontal swipes.
  main.style.touchAction = 'pan-y';

  let start = null;

  main.addEventListener(
    'touchstart',
    (event) => {
      if (event.touches.length !== 1) {
        start = null;
        return;
      }
      start = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    },
    { passive: true }
  );

  main.addEventListener('touchcancel', () => (start = null), { passive: true });

  main.addEventListener(
    'touchend',
    (event) => {
      if (!start) return;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      start = null;
      if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      step(dx < 0 ? 1 : -1);
    },
    { passive: true }
  );
})();
